import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export type MenuCategory = {
  id: string;
  name: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
};

export type MenuItem = {
  id: string;
  category_id: string;
  name: string;
  description: string | null;
  price: number;
  image_url: string | null;
  is_spicy: boolean;
  is_vegetarian: boolean;
  is_featured: boolean;
  is_available: boolean;
  allergens: string[] | null;
  preparation_time: number | null;
  display_order: number;
};

// Fallback data
export const fallbackCategories: MenuCategory[] = [
  {
    id: '1',
    name: 'Hookah',
    description: 'Premium tobacco blends and flavors',
    display_order: 1,
    is_active: true,
  },
  {
    id: '2',
    name: 'Food',
    description: 'Delicious appetizers and main courses',
    display_order: 2,
    is_active: true,
  },
  {
    id: '3',
    name: 'Drinks',
    description: 'Signature cocktails and beverages',
    display_order: 3,
    is_active: true,
  }
];

export const fallbackMenuItems: MenuItem[] = [
  // Hookah Items
  {
    id: '1',
    category_id: '1',
    name: 'Classic Mint',
    description: 'Refreshing mint flavor with a cool finish',
    price: 25,
    image_url: null,
    is_spicy: false,
    is_vegetarian: true,
    is_featured: true,
    is_available: true,
    allergens: null,
    preparation_time: 10,
    display_order: 1,
  },
  {
    id: '2',
    category_id: '1',
    name: 'Double Apple',
    description: 'Traditional blend with rich apple notes',
    price: 25,
    image_url: null,
    is_spicy: false,
    is_vegetarian: true,
    is_featured: false,
    is_available: true,
    allergens: null,
    preparation_time: 10,
    display_order: 2,
  },
  {
    id: '3',
    category_id: '1',
    name: 'Blue Mist',
    description: 'Sweet blueberry flavor with a hint of mint',
    price: 28,
    image_url: null,
    is_spicy: false,
    is_vegetarian: true,
    is_featured: true,
    is_available: true,
    allergens: null,
    preparation_time: 10,
    display_order: 3,
  },
  {
    id: '4',
    category_id: '1',
    name: 'Watermelon Chill',
    description: 'Juicy watermelon with a cooling effect',
    price: 28,
    image_url: null,
    is_spicy: false,
    is_vegetarian: true,
    is_featured: false,
    is_available: true,
    allergens: null,
    preparation_time: 10,
    display_order: 4,
  },
  {
    id: '5',
    category_id: '1',
    name: 'Citrus Paradise',
    description: 'Mix of zesty orange, lemon and lime',
    price: 28,
    image_url: null,
    is_spicy: false,
    is_vegetarian: true,
    is_featured: false,
    is_available: true,
    allergens: null,
    preparation_time: 10,
    display_order: 5,
  },
  {
    id: '6',
    category_id: '1',
    name: 'Seven8Nine Special',
    description: 'Our signature house blend (ask server for details)',
    price: 30,
    image_url: null,
    is_spicy: false,
    is_vegetarian: true,
    is_featured: true,
    is_available: true,
    allergens: null,
    preparation_time: 10,
    display_order: 6,
  },
  // Food Items
  {
    id: '7',
    category_id: '2',
    name: 'Hummus Plate',
    description: 'Creamy hummus with warm pita bread and olive oil',
    price: 12,
    image_url: null,
    is_spicy: false,
    is_vegetarian: true,
    is_featured: true,
    is_available: true,
    allergens: ['sesame'],
    preparation_time: 10,
    display_order: 1,
  },
  {
    id: '8',
    category_id: '2',
    name: 'Falafel Platter',
    description: 'Crispy falafel served with tahini sauce',
    price: 14,
    image_url: null,
    is_spicy: false,
    is_vegetarian: true,
    is_featured: false,
    is_available: true,
    allergens: ['sesame'],
    preparation_time: 15,
    display_order: 2,
  },
  {
    id: '9',
    category_id: '2',
    name: 'Chicken Shawarma',
    description: 'Tender marinated chicken with garlic sauce and pickles',
    price: 16,
    image_url: null,
    is_spicy: false,
    is_vegetarian: false,
    is_featured: true,
    is_available: true,
    allergens: ['gluten'],
    preparation_time: 20,
    display_order: 3,
  },
  {
    id: '10',
    category_id: '2',
    name: 'Mixed Grill',
    description: 'Selection of grilled meats with rice and vegetables',
    price: 24,
    image_url: null,
    is_spicy: false,
    is_vegetarian: false,
    is_featured: false,
    is_available: true,
    allergens: ['gluten'],
    preparation_time: 25,
    display_order: 4,
  },
  {
    id: '11',
    category_id: '2',
    name: 'Vegetarian Platter',
    description: 'Assortment of vegetarian mezze items',
    price: 18,
    image_url: null,
    is_spicy: false,
    is_vegetarian: true,
    is_featured: false,
    is_available: true,
    allergens: ['sesame'],
    preparation_time: 15,
    display_order: 5,
  },
  {
    id: '12',
    category_id: '2',
    name: 'Baklava',
    description: 'Sweet pastry with layers of nuts and honey',
    price: 8,
    image_url: null,
    is_spicy: false,
    is_vegetarian: true,
    is_featured: false,
    is_available: true,
    allergens: ['nuts', 'gluten'],
    preparation_time: 5,
    display_order: 6,
  },
  // Drinks Items
  {
    id: '13',
    category_id: '3',
    name: 'Moroccan Mint Tea',
    description: 'Traditional mint tea served in authentic teapot',
    price: 6,
    image_url: null,
    is_spicy: false,
    is_vegetarian: true,
    is_featured: true,
    is_available: true,
    allergens: null,
    preparation_time: 8,
    display_order: 1,
  },
  {
    id: '14',
    category_id: '3',
    name: 'Turkish Coffee',
    description: 'Strong coffee brewed in a cezve with cardamom',
    price: 5,
    image_url: null,
    is_spicy: false,
    is_vegetarian: true,
    is_featured: false,
    is_available: true,
    allergens: null,
    preparation_time: 10,
    display_order: 2,
  },
  {
    id: '15',
    category_id: '3',
    name: 'Lounge Lemonade',
    description: 'Fresh lemonade with mint and rose water',
    price: 7,
    image_url: null,
    is_spicy: false,
    is_vegetarian: true,
    is_featured: true,
    is_available: true,
    allergens: null,
    preparation_time: 5,
    display_order: 3,
  },
  {
    id: '16',
    category_id: '3',
    name: 'Mango Smoothie',
    description: 'Refreshing mango blend with a hint of honey',
    price: 8,
    image_url: null,
    is_spicy: false,
    is_vegetarian: true,
    is_featured: false,
    is_available: true,
    allergens: null,
    preparation_time: 5,
    display_order: 4,
  },
  {
    id: '17',
    category_id: '3',
    name: 'Seven8Nine Signature Mocktail',
    description: 'Our special non-alcoholic house blend',
    price: 9,
    image_url: null,
    is_spicy: false,
    is_vegetarian: true,
    is_featured: true,
    is_available: true,
    allergens: null,
    preparation_time: 8,
    display_order: 5,
  },
  {
    id: '18',
    category_id: '3',
    name: 'Sparkling Water',
    description: 'Premium sparkling water with or without flavor',
    price: 4,
    image_url: null,
    is_spicy: false,
    is_vegetarian: true,
    is_featured: false,
    is_available: true,
    allergens: null,
    preparation_time: 2,
    display_order: 6,
  }
];

// Hook to fetch menu categories
export const useMenuCategories = () => {
    
  return useQuery({
    queryKey: ['menuCategories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('menu_categories')
        .select('*')
        .order('display_order', { ascending: true })
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching menu categories:', error);
        return fallbackCategories;
      }

      return data.length > 0 ? data : fallbackCategories;
    },
    initialData: fallbackCategories,
  });
};

// Hook to fetch menu items
export const useMenuItems = () => {
  return useQuery({
    queryKey: ['menuItems'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('menu_items')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching menu items:', error);
        return fallbackMenuItems;
      }

      return data.length > 0 ? data : fallbackMenuItems;
    },
    initialData: fallbackMenuItems,
  });
};

// Helper functions
export const getMenuItemsByCategory = (items: MenuItem[], categoryId: string) => {
  return items.filter(item => item.category_id === categoryId);
};

export const getFeaturedItems = (items: MenuItem[]) => {
  return items.filter(item => item.is_featured);
};

export const getCategoryById = (categories: MenuCategory[], categoryId: string) => {
  return categories.find(category => category.id === categoryId);
}; 